import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import AddPhotoAlternateRounded from '@mui/icons-material/AddPhotoAlternateRounded';
import CloudDownloadRounded from '@mui/icons-material/CloudDownloadRounded';
import { uploadImage } from '../../services/uploadsApi';

// `onFetchFromShopify` (optional) — when provided, shows a "Get images from
// Shopify" button; the callback returns an array of image URLs to append.
const ImageGallery = ({ images = [], onChange, readOnly = false, folder = 'products', onFetchFromShopify }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [fetchingShopify, setFetchingShopify] = useState(false);

  const addUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    onChange([...images, url]);
    setUrlInput('');
  };

  const fetchFromShopify = async () => {
    if (!onFetchFromShopify) return;
    setFetchingShopify(true);
    try {
      const urls = await onFetchFromShopify();
      const existing = new Set(images.map((i) => (typeof i === 'string' ? i : i?.src)));
      const fresh = (urls || []).filter((u) => u && !existing.has(u));
      if (fresh.length) onChange([...images, ...fresh]);
    } catch (err) {
      console.error('Fetch from Shopify failed:', err);
    } finally {
      setFetchingShopify(false);
    }
  };
  // Drag-to-reorder state (native HTML5 DnD). dragIndex = item being dragged,
  // overIndex = the slot it's hovering, for visual feedback.
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const handleRemove = (index) => {
    if (readOnly) return;
    const updated = images.filter((_, i) => i !== index);
    onChange(updated);
  };

  const moveImage = (from, to) => {
    if (from === to || from == null || to == null) return;
    const updated = [...images];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    onChange(updated);
  };

  const handleDrop = (index) => {
    moveImage(dragIndex, index);
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        const url = await uploadImage(file, folder);
        urls.push(url);
      }
      if (urls.length > 0) onChange([...images, ...urls]);
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
      {images.length === 0 && readOnly && (
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 1,
            border: '2px dashed',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ImageRounded sx={{ color: 'text.secondary', fontSize: 28 }} />
        </Box>
      )}
      {images.map((img, index) => {
        const src = typeof img === 'string' ? img : img?.src;
        const alt = typeof img === 'string' ? '' : (img?.alt || '');
        return (
          <Box
            key={index}
            draggable={!readOnly}
            onDragStart={() => !readOnly && setDragIndex(index)}
            onDragOver={(e) => {
              if (readOnly) return;
              e.preventDefault();
              if (overIndex !== index) setOverIndex(index);
            }}
            onDrop={() => !readOnly && handleDrop(index)}
            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
            sx={{
              position: 'relative',
              width: 80,
              height: 80,
              borderRadius: 1,
              overflow: 'hidden',
              border: '2px solid',
              borderColor: overIndex === index && dragIndex !== null ? 'primary.main' : 'divider',
              opacity: dragIndex === index ? 0.4 : 1,
              cursor: readOnly ? 'default' : 'grab',
            }}
          >
            <Box
              component="img"
              src={src}
              alt={alt}
              onClick={() => setLightboxSrc(src)}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }}
            />
            {!readOnly && (
              <IconButton
                size="small"
                onClick={() => handleRemove(index)}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: '#fff',
                  p: 0.25,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <CloseRounded sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        );
      })}
      {!readOnly && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFiles}
          />
          <Box
            onClick={() => !uploading && inputRef.current?.click()}
            sx={{
              width: 80,
              height: 80,
              borderRadius: 1,
              border: '2px dashed',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              cursor: uploading ? 'wait' : 'pointer',
              color: 'text.secondary',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }}
          >
            {uploading ? (
              <CircularProgress size={22} />
            ) : (
              <>
                <AddPhotoAlternateRounded sx={{ fontSize: 26 }} />
                <Box sx={{ fontSize: '0.65rem', fontWeight: 600 }}>Upload</Box>
              </>
            )}
          </Box>
        </>
      )}
    </Box>

    {/* Add by URL + pull from the connected Shopify store */}
    {!readOnly && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1.5 }}>
        <TextField
          size="small"
          placeholder="Paste an image URL…"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <Button size="small" variant="outlined" onClick={addUrl} disabled={!urlInput.trim()} sx={{ textTransform: 'none' }}>
          Add URL
        </Button>
        {onFetchFromShopify && (
          <Button
            size="small"
            variant="outlined"
            startIcon={fetchingShopify ? <CircularProgress size={14} /> : <CloudDownloadRounded />}
            onClick={fetchFromShopify}
            disabled={fetchingShopify}
            sx={{ textTransform: 'none' }}
          >
            {fetchingShopify ? 'Fetching…' : 'Get images from Shopify'}
          </Button>
        )}
      </Box>
    )}

    {/* Lightbox — click any image to view it larger; click anywhere to close. */}
    {lightboxSrc && (
      <Box
        onClick={() => setLightboxSrc(null)}
        sx={{
          position: 'fixed', inset: 0, zIndex: 1400,
          bgcolor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3,
          cursor: 'zoom-out',
        }}
      >
        <IconButton
          onClick={() => setLightboxSrc(null)}
          sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
        >
          <CloseRounded />
        </IconButton>
        <Box
          component="img"
          src={lightboxSrc}
          alt=""
          onClick={(e) => e.stopPropagation()}
          sx={{ maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 2, boxShadow: 24, cursor: 'default' }}
        />
      </Box>
    )}
    </>
  );
};

export default ImageGallery;
